import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-badge';
import { toast } from '../ui/use-toast';
import {
  Plus,
  X,
  Upload,
  ImageIcon,
  Building2,
  Package,
  Tag,
  DollarSign,
  FileText,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { SupplierProductsAPI } from '../../lib/api';
import { SupplierProductCreateRequest, AvailableSupplier, AvailableCategory } from '../../lib/types';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection, FormGrid } from '../ui/form-field';
import { CurrencyInput } from '../ui/currency-input';
import { NumberInput } from '../ui/number-input';

interface SupplierProductCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function SupplierProductCreateModal({
  isOpen,
  onClose,
  onSuccess
}: SupplierProductCreateModalProps) {
  // Form state
  const [formData, setFormData] = useState<SupplierProductCreateRequest>({
    supplierId: '',
    name: '',
    description: '',
    categoryId: '',
    unit: '',
    price: 0,
    maxQuantity: 1000,
    minOrderQuantity: 1,
    initialStock: 0,
    lowStockThreshold: 10,
    reorderPoint: 5,
    specifications: [],
    hsnCode: '',
    gstRate: 18,
    adminNotes: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<AvailableSupplier[]>([]);
  const [categories, setCategories] = useState<AvailableCategory[]>([]);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Specifications management
  const [newSpec, setNewSpec] = useState({ name: '', value: '' });

  // Common units
  const commonUnits = [
    'ton', 'kg', 'cubic_meter', 'cubic_meters', 'bag', 'pieces',
    'square_meters', 'square_feet', 'cubic_feet', 'liters', 'meters', 'sqft'
  ];

  // Load initial data
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    try {
      const [suppliersResponse, categoriesResponse] = await Promise.all([
        SupplierProductsAPI.getAvailableSuppliers({ limit: 100, verificationStatus: 'verified' }),
        SupplierProductsAPI.getAvailableCategories({ limit: 100 })
      ]);

      if (suppliersResponse.success) {
        setSuppliers(suppliersResponse.data.suppliers);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data.categories);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: "Error",
        description: "Failed to load suppliers and categories",
        variant: "destructive"
      });
    }
  };

  // Handle image selection
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image file`,
          variant: "destructive"
        });
        return false;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "File too large",
          description: `${file.name} is larger than 5MB`,
          variant: "destructive"
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...validFiles]);

      // Create preview URLs
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreviewUrls(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }

    // Reset input
    event.target.value = '';
  };

  // Remove image
  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Add specification
  const addSpecification = () => {
    if (newSpec.name.trim() && newSpec.value.trim()) {
      setFormData(prev => ({
        ...prev,
        specifications: [...(prev.specifications || []), { ...newSpec }]
      }));
      setNewSpec({ name: '', value: '' });
    }
  };

  // Remove specification
  const removeSpecification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      specifications: prev.specifications?.filter((_, i) => i !== index) || []
    }));
  };

  // Update form data
  const updateFormData = (field: keyof SupplierProductCreateRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierId) newErrors.supplierId = 'Supplier is required';
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.categoryId) newErrors.categoryId = 'Category is required';
    if (!formData.unit) newErrors.unit = 'Unit is required';
    if (!formData.price || formData.price <= 0) newErrors.price = 'Price must be greater than 0';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      let response;

      // Check if we have images to upload
      if (selectedImages.length > 0) {
        // Use FormData for file upload
        const formDataToSend = new FormData();

        // Add all form fields
        Object.entries(formData).forEach(([key, value]) => {
          if (key === 'specifications') {
            if (value && Array.isArray(value) && value.length > 0) {
              formDataToSend.append(key, JSON.stringify(value));
            }
          } else if (value !== undefined && value !== null) {
            formDataToSend.append(key, value.toString());
          }
        });

        // Add image files
        selectedImages.forEach((file) => {
          formDataToSend.append('productImages', file);
        });

        response = await SupplierProductsAPI.createWithFiles(formDataToSend);
      } else {
        // Use regular JSON API if no images
        response = await SupplierProductsAPI.create(formData);
      }

      if (response.success) {
        toast({
          title: "Success",
          description: `Product created successfully${response.data.imagesUploaded ? ` with ${response.data.imagesUploaded} images` : ''}`,
          variant: "default"
        });

        handleClose();
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to create product",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Error creating product:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create product. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form and image state when closing
    setFormData({
      supplierId: '',
      name: '',
      description: '',
      categoryId: '',
      unit: '',
      price: 0,
      maxQuantity: 1000,
      minOrderQuantity: 1,
      initialStock: 0,
      lowStockThreshold: 10,
      reorderPoint: 5,
      specifications: [],
      hsnCode: '',
      gstRate: 18,
      adminNotes: ''
    });
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setNewSpec({ name: '', value: '' });
    setErrors({});
    onClose();
  };

  return (
    <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
      <EnhancedDialogContent size="3xl">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Create New Product for Supplier
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Create a new product and assign it to a supplier with inventory management
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <EnhancedDialogBody>
            {/* Supplier Selection */}
            <FormSection
              title="Supplier Information"
              icon={<Building2 className="h-5 w-5" />}
              description="Select the supplier for this product"
            >
              <FormField
                label="Select Supplier"
                required
                error={errors.supplierId}
              >
                <Select
                  value={formData.supplierId}
                  onValueChange={(value) => updateFormData('supplierId', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a verified supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier._id} value={supplier._id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{supplier.businessName}</span>
                          <div className="flex items-center gap-2 ml-2">
                            {supplier.rating && (
                              <Badge variant="outline" className="text-xs">
                                ⭐ {supplier.rating.toFixed(1)}
                              </Badge>
                            )}
                            <StatusBadge status={supplier.verificationStatus} className="text-xs" />
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </FormSection>

            {/* Product Basic Information */}
            <FormSection
              title="Product Information"
              icon={<Tag className="h-5 w-5" />}
              description="Basic product details and categorization"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Product Name"
                  required
                  error={errors.name}
                >
                  <Input
                    value={formData.name}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    placeholder="Enter product name"
                  />
                </FormField>

                <FormField
                  label="Category"
                  required
                  error={errors.categoryId}
                >
                  <Select
                    value={formData.categoryId}
                    onValueChange={(value) => updateFormData('categoryId', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category._id} value={category._id}>
                          <div>
                            {category.name}
                            {category.parent && (
                              <span className="text-muted-foreground ml-2">
                                ({category.parent.name})
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </FormGrid>

              <FormField
                label="Description"
                hint="Detailed product description for customers"
              >
                <Textarea
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  placeholder="Enter detailed product description"
                  rows={3}
                />
              </FormField>

              <FormGrid columns={3}>
                <FormField
                  label="Unit"
                  required
                  error={errors.unit}
                >
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => updateFormData('unit', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {commonUnits.map(unit => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField
                  label="HSN Code"
                  hint="Harmonized System of Nomenclature code"
                >
                  <Input
                    value={formData.hsnCode}
                    onChange={(e) => updateFormData('hsnCode', e.target.value)}
                    placeholder="Enter HSN code"
                  />
                </FormField>

                <FormField
                  label="GST Rate"
                  hint="Goods and Services Tax rate"
                >
                  <Select
                    value={formData.gstRate?.toString() || "18"}
                    onValueChange={(value) => updateFormData('gstRate', Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select GST rate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="18">18%</SelectItem>
                      <SelectItem value="28">28%</SelectItem>
                      <SelectItem value="0">0%</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </FormGrid>
            </FormSection>

            {/* Pricing and Quantity */}
            <FormSection
              title="Pricing & Quantity"
              icon={<span className="text-lg">₹</span>}
              description="Set pricing and quantity constraints"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Base Price"
                  hint="Leave empty to auto-calculate from selling price"
                >
                  <CurrencyInput
                    value={formData.basePrice || 0}
                    onChange={(value) => updateFormData('basePrice', value || undefined)}
                    placeholder="Auto-calculated"
                    minValue={0}
                  />
                </FormField>

                <FormField
                  label="Selling Price"
                  required
                  error={errors.price}
                  hint="Supplier's selling price for this product"
                >
                  <CurrencyInput
                    value={formData.price}
                    onChange={(value) => updateFormData('price', value)}
                    placeholder="0.00"
                    minValue={0}
                    error={errors.price}
                  />
                </FormField>
              </FormGrid>

              <FormGrid columns={4}>
                <FormField
                  label="Min Order Quantity"
                  hint="Minimum quantity per order"
                >
                  <NumberInput
                    value={formData.minOrderQuantity}
                    onChange={(value) => updateFormData('minOrderQuantity', value)}
                    placeholder="1"
                    minValue={1}
                    allowDecimals={false}
                  />
                </FormField>

                <FormField
                  label="Max Order Quantity"
                  hint="Maximum quantity per order"
                >
                  <NumberInput
                    value={formData.maxQuantity}
                    onChange={(value) => updateFormData('maxQuantity', value)}
                    placeholder="1000"
                    minValue={1}
                    allowDecimals={false}
                  />
                </FormField>

                <FormField
                  label="Initial Stock"
                  hint="Starting inventory quantity"
                >
                  <NumberInput
                    value={formData.initialStock}
                    onChange={(value) => updateFormData('initialStock', value)}
                    placeholder="0"
                    minValue={0}
                    allowDecimals={false}
                  />
                </FormField>

                <FormField
                  label="Low Stock Threshold"
                  hint="Alert when stock falls below this"
                >
                  <NumberInput
                    value={formData.lowStockThreshold}
                    onChange={(value) => updateFormData('lowStockThreshold', value)}
                    placeholder="10"
                    minValue={0}
                    allowDecimals={false}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            {/* Specifications */}
            <FormSection
              title="Product Specifications"
              icon={<FileText className="h-5 w-5" />}
              description="Add technical specifications and features"
              collapsible
              defaultExpanded={false}
            >
              {/* Add new specification */}
              <div className="flex gap-2">
                <Input
                  placeholder="Specification name"
                  value={newSpec.name}
                  onChange={(e) => setNewSpec(prev => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  placeholder="Specification value"
                  value={newSpec.value}
                  onChange={(e) => setNewSpec(prev => ({ ...prev, value: e.target.value }))}
                />
                <Button type="button" onClick={addSpecification} variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Existing specifications */}
              {formData.specifications && formData.specifications.length > 0 && (
                <div className="space-y-2">
                  {formData.specifications.map((spec, index) => (
                    <div key={index} className="flex items-center justify-between bg-muted p-3 rounded-md">
                      <div>
                        <span className="font-medium">{spec.name}:</span>
                        <span className="ml-2">{spec.value}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSpecification(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </FormSection>

            {/* Images */}
            <FormSection
              title="Product Images"
              icon={<ImageIcon className="h-5 w-5" />}
              description="Upload product images (optional)"
              collapsible
              defaultExpanded={false}
            >
              <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Click to upload images or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, JPEG up to 5MB each
                  </p>
                </label>
              </div>

              {/* Image previews */}
              {imagePreviewUrls.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-20 object-cover rounded-md"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                      {index === 0 && (
                        <Badge className="absolute bottom-1 left-1 text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </FormSection>

            {/* Admin Notes */}
            <FormSection
              title="Admin Notes"
              icon={<FileText className="h-5 w-5" />}
              description="Internal notes and instructions"
              collapsible
              defaultExpanded={false}
            >
              <FormField
                label="Administrative Notes"
                hint="Add any administrative notes or special instructions"
              >
                <Textarea
                  value={formData.adminNotes}
                  onChange={(e) => updateFormData('adminNotes', e.target.value)}
                  placeholder="Add any administrative notes or special instructions..."
                  rows={3}
                />
              </FormField>
            </FormSection>
          </EnhancedDialogBody>

          <EnhancedDialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.supplierId || !formData.name || !formData.categoryId || !formData.unit}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Create Product
                </>
              )}
            </Button>
          </EnhancedDialogFooter>
        </form>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
} 